import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { 
  Search, 
  Play, 
  X 
} from 'lucide-react';

import Button from '../../../../components/buttons/primaryButton';
import {
  getVideoLibrary,
  searchVideos
} from '../../../../redux/actions/api/apiAction';
import { 
  getCategoryHierarchy,
  formatCompactCategoryDisplay 
} from '../../../../utils';

const VideosLibraryPopup = ({ 
  isOpen, 
  toggle, 
  onSelectVideos, 
  selectedVideos = [], 
  title = "Select Video(s)" 
}) => {
  const dispatch = useDispatch();
  const { videosLibraryData, videosLibraryDataCount } = useSelector(
    state => state.videosLibrary
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [localSelectedVideos, setLocalSelectedVideos] = useState([]);
  const [playingVideoId, setPlayingVideoId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setLocalSelectedVideos([...selectedVideos]);
      loadVideos();
    }
  }, [isOpen, selectedVideos]);

  useEffect(() => {
    if (!isOpen) return;
    
    const timeoutId = setTimeout(() => {
      if (searchTerm || selectedCategory) {
        handleSearch();
      } else {
        loadVideos(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedCategory, isOpen]);

  const loadVideos = async (page = 1) => {
    setIsLoading(true);
    try {
      const searchFilters = {
        page,
        limit: 12
      };

      if (searchTerm && searchTerm.trim()) {
        searchFilters.title = searchTerm.trim();
      }

      if (selectedCategory && selectedCategory !== 'All Categories') {
        searchFilters.category = selectedCategory;
      }

      await dispatch(getVideoLibrary(searchFilters));
      setCurrentPage(page);
    } catch (error) {
      console.error('Error loading videos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadVideos(1);
  };

  const handleCategoryChange = (event) => {
    const value = event?.target?.value || '';
    setSelectedCategory(value);
  };

  const toggleVideoSelection = (video) => {
    const videoId = video.id || video._id;
    const isSelected = localSelectedVideos.some(v => (v.id || v._id) === videoId);
    
    if (isSelected) {
      setLocalSelectedVideos(prev => 
        prev.filter(v => (v.id || v._id) !== videoId)
      );
    } else {
      setLocalSelectedVideos(prev => [...prev, video]);
    }
  };

  const isVideoSelected = (video) => {
    const videoId = video.id || video._id;
    return localSelectedVideos.some(v => (v.id || v._id) === videoId);
  };

  const handlePlayVideo = (video) => {
    const videoId = video.id || video._id;
    if (playingVideoId === videoId) {
      setPlayingVideoId(null);
    } else {
      setPlayingVideoId(videoId);
    }
  };

  const handleConfirmSelection = () => {
    onSelectVideos(localSelectedVideos);
    handleCancel();
  };

  const handleCancel = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setLocalSelectedVideos([]);
    setPlayingVideoId(null);
    toggle();
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const renderCategorySelect = (value, onChange, className, includeAllCategories = false) => {
    const categoryHierarchy = getCategoryHierarchy();
    
    return (
      <select value={value} onChange={onChange} className={className}>
        <option value="">
          {includeAllCategories ? 'All Categories' : 'Select a category'}
        </option>
        
        {Object.entries(categoryHierarchy).map(([groupName, categories]) => (
          <optgroup key={groupName} label={groupName}>
            {categories.map(category => {
              const displayName = category.includes(' - ') 
                ? category.split(' - ')[1] 
                : category;
                
              return (
                <option key={category} value={category}>
                  {displayName}
                </option>
              );
            })}
          </optgroup>
        ))}
      </select>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleCancel}
      className="video-library-container video-library-popup"
    //   backdrop="static"
    >
      <ModalHeader toggle={handleCancel}>
        <div className="d-flex align-items-center">
          <h4 className="mb-0 me-2">{title}</h4>
        </div>
      </ModalHeader>

      <ModalBody className="video-library-popup-body">
        {/* Filters Section */}
        <div className="mb-4">
          <div className="filters-header d-flex justify-content-between align-items-center mb-3">
            <h5>
              Videos ({videosLibraryDataCount || (Array.isArray(videosLibraryData) ? videosLibraryData.length : 0)})
            </h5>
            <div className="filters-controls">
                <div className="search-input me-3">
                    <Search size={16} className="search-icon" />
                    <input
                    type="text"
                    placeholder="Search videos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value || '')}
                    />
                </div>
                {renderCategorySelect(
                  selectedCategory, 
                  handleCategoryChange, 
                  "category-select", 
                  true
                )}
            </div>
          </div>
        </div>

        {/* Videos Grid */}
        <div className="videos-grid popup-grid">
          {isLoading && (!Array.isArray(videosLibraryData) || videosLibraryData.length === 0) ? (
            <div className="loading-state text-center py-4 d-flex justify-content-center align-items-center">
              Loading videos...
            </div>
          ) : !Array.isArray(videosLibraryData) || videosLibraryData.length === 0 ? (
            <div className="empty-state text-center py-4">
              {searchTerm || (selectedCategory && selectedCategory !== 'All Categories') 
                ? 'No videos found matching your criteria.' 
                : 'No videos uploaded yet.'
              }
            </div>
          ) : (
            videosLibraryData.map((video) => {
              const isSelected = isVideoSelected(video);
              const videoId = video.id || video._id;
              
              return (
                <div
                  key={videoId}
                  className={`video-card popup-card ${isSelected ? 'selected' : ''} ${playingVideoId === videoId ? 'is-playing' : ''}`}
                  onClick={() => toggleVideoSelection(video)}
                >
                  {/* Selection Checkbox */}
                  <div className="selection-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleVideoSelection(video)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  <div className="video-thumbnail">
                    {playingVideoId === videoId ? (
                      <video
                        className="video-player"
                        src={video.url}
                        controls
                        autoPlay
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <img 
                        src={video.thumbnailUrl || '/api/placeholder/280/160'} 
                        alt={video.title || 'Video thumbnail'}
                        onError={(e) => {
                          e.target.src = '/api/placeholder/280/160';
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPlayingVideoId(videoId);
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    )}

                    <div className="video-duration">
                      {formatDuration(video.duration)}
                    </div>
                    <div className="play-overlay">
                      <button 
                        className="play-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayVideo(video);
                        }}
                      >
                        <Play size={20} fill="currentColor" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="video-info">
                    <div className='d-flex justify-content-between align-items-center flex-wrap'>
                      <div className="video-title">{video.title || ""}</div>
                      <div className="video-category">
                        {(() => {
                          const categoryInfo = formatCompactCategoryDisplay(video.category);
                          if (!categoryInfo.main) return "";

                          return (
                            <div className={`category-display pill-style`}>
                              <span className="main-category">{categoryInfo.main}</span>
                              {categoryInfo.sub && (
                                <span className="sub-category"> - {categoryInfo.sub}</span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="video-description">
                      {video.description || ""}
                    </div>
                    {video.fileSize && (
                      <div className="video-file-size">
                        {formatFileSize(video.fileSize)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          type="button"
          value="Cancel"
          styleClass="btn-transparent"
          onClick={handleCancel}
        />
        <Button
          type="button"
          value={`Add (${localSelectedVideos.length}) videos`}
          onClick={handleConfirmSelection}
          disabled={localSelectedVideos.length === 0}
        />
      </ModalFooter>
    </Modal>
  );
};

export default VideosLibraryPopup;